import React from 'react';
import ReactDOM from 'react-dom';
import { type IClusterMethod } from '@yandex/ymaps3-clusterer';
import {
  type GenericReactify,
  type Reactify
} from '@yandex/ymaps3-types/reactify';

interface IYmapsInitResult {
  reactify: Reactify;
  clusterer: typeof import('@yandex/ymaps3-clusterer');
  clusterByGrid: (params: { gridSize: number }) => IClusterMethod;
}

let ymapsReact: GenericReactify | null = null;
let ymapsInitialized = false;
let scriptLoadPromise: Promise<void> | null = null;
let scriptLoadFailed = false;
let initPromise: Promise<IYmapsInitResult | null> | null = null;

const SCRIPT_URL = 'https://api-maps.yandex.ru/3.0/';
const SCRIPT_DATA_ATTR = 'data-ymaps3-loader';

const loadYmapsScript = (apiKey: string): Promise<void> => {
  if (typeof ymaps3 !== 'undefined') {
    return Promise.resolve();
  }

  if (scriptLoadPromise !== null) {
    return scriptLoadPromise;
  }

  // HMR-сценарий: модульный кэш scriptLoadPromise обнуляется при перезагрузке
  // модуля, но script-тег остаётся в <head>. Если глобал ymaps3 ещё не появился
  // (скрипт грузится прямо сейчас) — подключаемся к существующему тегу через
  // load/error, чтобы не вставить второй и не получить "ymaps3: already defined".
  const existingScript = document.querySelector<HTMLScriptElement>(
    `script[${SCRIPT_DATA_ATTR}]`
  );
  if (existingScript !== null) {
    if (scriptLoadFailed) {
      // Предыдущая загрузка завершилась ошибкой: удаляем мёртвый тег и
      // создаём новый ниже, иначе Promise зависнет навсегда.
      existingScript.remove();
      scriptLoadFailed = false;
    } else {
      scriptLoadPromise = new Promise<void>((resolve, reject) => {
        existingScript.addEventListener('load', () => resolve(), {
          once: true
        });
        existingScript.addEventListener(
          'error',
          () => reject(new Error('Не удалось загрузить скрипт Яндекс Карт')),
          { once: true }
        );
      });
      return scriptLoadPromise;
    }
  }

  scriptLoadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${SCRIPT_URL}?apikey=${apiKey}&lang=ru_RU`;
    script.setAttribute(SCRIPT_DATA_ATTR, 'true');
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadPromise = null;
      scriptLoadFailed = true;
      reject(new Error('Не удалось загрузить скрипт Яндекс Карт'));
    };
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
};

/**
 * Инициализирует SDK Яндекс Карт 3.0: загружает скрипт в head, поднимает
 * reactify-обёртку и модуль кластеризации. Результат кэшируется в модульной
 * области, повторные вызовы возвращают тот же экземпляр без повторной загрузки.
 *
 * @returns объект с reactify, модулем кластеризации и фабрикой clusterByGrid;
 *   null если ключ API не задан в окружении
 */
export const initYmaps = (): Promise<IYmapsInitResult | null> => {
  const apiKey = import.meta.env.VITE_YANDEX_MAPS_API_KEY;
  if (apiKey === '' || apiKey === undefined) {
    return Promise.resolve(null);
  }

  if (initPromise !== null) {
    return initPromise;
  }

  initPromise = (async (): Promise<IYmapsInitResult | null> => {
    try {
      if (ymapsInitialized && ymapsReact !== null) {
        const clustererModule = await import('@yandex/ymaps3-clusterer');
        return {
          reactify: ymapsReact.bindTo(React, ReactDOM),
          clusterer: clustererModule,
          clusterByGrid: clustererModule.clusterByGrid
        };
      }

      await loadYmapsScript(apiKey);
      await ymaps3.ready;

      const [reactifyImport, clustererModule] = await Promise.all([
        ymaps3.import('@yandex/ymaps3-reactify'),
        import('@yandex/ymaps3-clusterer')
      ]);

      ymapsReact = reactifyImport.reactify;
      ymapsInitialized = true;

      return {
        reactify: ymapsReact.bindTo(React, ReactDOM),
        clusterer: clustererModule,
        clusterByGrid: clustererModule.clusterByGrid
      };
    } catch (error) {
      initPromise = null;
      throw error;
    }
  })();

  return initPromise;
};
